import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { docsApi } from '../api';
import DocumentCard from '../components/documents/DocumentCard';
import DocumentModal from '../components/documents/DocumentModal';
import DocumentDetailModal from '../components/documents/DocumentDetailModal';
import ConfirmDialog from '../components/common/ConfirmDialog';
import TagInput from '../components/common/TagInput';
import { toast } from 'react-toastify';

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const [query,   setQuery]   = useState(searchParams.get('q') ?? '');
  const [tags,    setTags]    = useState([]);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const [editDoc,   setEditDoc]   = useState(null);
  const [viewDoc,   setViewDoc]   = useState(null);
  const [deleteDoc, setDeleteDoc] = useState(null);

  // 1. Add a Set to track viewed documents
  const [viewedDocs, setViewedDocs] = useState(new Set());

  const doSearch = useCallback(async (q, t) => {
    if (!q?.trim() && t.length === 0) return;
    setLoading(true);
    setSearched(true);
    setViewedDocs(new Set()); // 2. Reset the tracking Set on a new search
    try {
      const payload = {};
      if (q?.trim()) payload.query = q.trim();
      if (t.length)  payload.tags  = t;
      const { data } = await docsApi.search({ ...payload, page: 0, size: 50 });
      setResults(data);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Auto-search if navigated with ?q=
  useEffect(() => {
    const q = searchParams.get('q');
    if (q) { setQuery(q); doSearch(q, []); }
  }, []); // eslint-disable-line

  const handleSearch = (e) => {
    e?.preventDefault();
    setSearchParams(query ? { q: query } : {});
    doSearch(query, tags);
  };

  const handleDelete = async () => {
    try {
      await docsApi.delete(deleteDoc.id);
      toast.success('Deleted');
      setDeleteDoc(null);
      setResults(r => r.filter(d => d.id !== deleteDoc.id));
    } catch {
      toast.error('Failed to delete');
    }
  };

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">Search</h1>
          <p className="page-subtitle">Full-text search with optional tag filters</p>
        </div>
      </div>

      {/* Search Form */}
      <div className="search-bar-container">
        <form onSubmit={handleSearch}>
          <div className="form-group" style={{ marginBottom: '0.75rem' }}>
            <label className="form-label">Search Query</label>
            <input
              className="form-input"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search by keywords, phrases, or content..."
              autoFocus
            />
          </div>
          <div className="form-group" style={{ marginBottom: '0.75rem' }}>
            <label className="form-label">Filter by Tags <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(optional — AND logic)</span></label>
            <TagInput tags={tags} onChange={setTags} placeholder="Add tags to filter..." />
          </div>
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
            <button className="btn-primary-custom" type="submit" disabled={loading || (!query.trim() && tags.length === 0)}>
              {loading ? <><span className="spinner" /> Searching…</> : '⌕ Search'}
            </button>
            {(query || tags.length > 0) && (
              <button
                type="button"
                className="btn-ghost"
                onClick={() => { setQuery(''); setTags([]); setResults([]); setSearched(false); setSearchParams({}); }}
              >
                Clear
              </button>
            )}
            {searched && !loading && (
              <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginLeft: 'auto' }}>
                {results.length} result{results.length !== 1 ? 's' : ''}
              </span>
            )}
          </div>
        </form>
      </div>

      {/* Results */}
      {loading && <div className="loading-overlay"><span className="spinner" /></div>}

      {!loading && searched && results.length === 0 && (
        <div className="empty-state">
          <div className="empty-state-icon">🔍</div>
          <div className="empty-state-title">No results found</div>
          <div className="empty-state-text">Try different keywords or remove some tag filters</div>
        </div>
      )}

      {!loading && results.length > 0 && (
        <div className="row g-3">
          {results.map(doc => (
            <div key={doc.id} className="col-12 col-sm-6 col-lg-4">
              <DocumentCard
                doc={doc}
                onClick={async () => {
                  // Only fetch from API if not viewed in this search session
                  if (!viewedDocs.has(doc.id)) {
                    try {
                      // This API call triggers the VIEW event on the backend
                      const { data } = await docsApi.getById(doc.id);
                      setViewDoc(data);
                      setViewedDocs(prev => new Set(prev).add(doc.id));
                    } catch (error) {
                      setViewDoc(doc);
                    }
                  } else {
                    // Fallback to local doc data since event was already fired
                    setViewDoc(doc);
                  }
                }}
                onEdit={d => setEditDoc(d)}
                onDelete={d => setDeleteDoc(d)}
              />
            </div>
          ))}
        </div>
      )}

      {!searched && (
        <div className="empty-state" style={{ paddingTop: '2rem' }}>
          <div className="empty-state-icon" style={{ opacity: 0.2 }}>⌕</div>
          <div className="empty-state-title">Enter a query or tags to search</div>
          <div className="empty-state-text">Partial matches and fuzzy search are supported</div>
        </div>
      )}

      {editDoc && <DocumentModal doc={editDoc} onClose={() => setEditDoc(null)} onSaved={() => doSearch(query, tags)} />}
      {viewDoc && (
        <DocumentDetailModal
          doc={viewDoc}
          onClose={() => setViewDoc(null)}
          onEdit={d => { setViewDoc(null); setEditDoc(d); }}
        />
      )}
      {deleteDoc && (
        <ConfirmDialog
          title="Delete Document"
          message={`"${deleteDoc.title}" will be permanently deleted.`}
          onConfirm={handleDelete}
          onCancel={() => setDeleteDoc(null)}
        />
      )}
    </>
  );
}
